import {useState, useEffect, useCallback} from "react";
import {
  CreateThread,
  PostMessageToAssistant,
  GetAssistantMessages,
  UpsertConversationLog,
} from "../../services/backend";
import {getLocalStorageItem, setLocalStorageItem} from "src/utilities/cookie";
import {AssistantSettings} from "src/interfaces/settings";
import {AssistantChannel, AssistantChatMessage, Thread} from "src/interfaces/assistant";
import {ConversationLogStatus, ConversationMessage} from "src/interfaces/conversationLog";

export const useThread = (
  knowledgeBaseId: number,
  assistantSettings: AssistantSettings | undefined,
  maxThreadAge: number,
  channel: AssistantChannel,
) => {
  const [thread, setThread] = useState<Thread | null>(null);
  const [isEmailValidated, setIsEmailValidated] = useState<boolean>(false);

  const initializeThread = useCallback(async () => {
    try {
      const assistantId = assistantSettings?.assistants.find((assistant) => assistant.channel === channel)?.id;
      if (!assistantId) return;

      const response = await CreateThread(knowledgeBaseId);
      const newThread = {
        threadId: response.data.thread_id,
        createdAt: new Date().toISOString(),
        isFeedbackGiven: false,
        hasValidatedEmail: false,
      };
      setLocalStorageItem(`assistantThread-${channel}-${knowledgeBaseId}`, JSON.stringify(newThread));
      setThread(newThread);
    } catch (error) {
      console.error("Error creating thread:", error);
    }
  }, [knowledgeBaseId, assistantSettings, channel]);

  const isThreadExpired = useCallback(
    (createdAt: string): boolean => {
      const threadDate = new Date(createdAt);
      const now = new Date();
      const hoursDiff = (now.getTime() - threadDate.getTime()) / (1000 * 60 * 60);

      if (maxThreadAge === 0 || undefined) return false;
      return hoursDiff > maxThreadAge;
    },
    [maxThreadAge],
  );

  const validateEmail = useCallback(() => {
    if (thread) {
      const updatedThread = {...thread, hasValidatedEmail: true};
      setLocalStorageItem(`assistantThread-${channel}-${knowledgeBaseId}`, JSON.stringify(updatedThread));
      setThread(updatedThread);
      setIsEmailValidated(true);
    }
  }, [thread, channel, knowledgeBaseId]);

  useEffect(() => {
    const storedThread = getLocalStorageItem<Thread | null>(
      `assistantThread-${channel}-${knowledgeBaseId}`,
      null,
      true,
    );
    if (storedThread && storedThread.threadId && !isThreadExpired(storedThread.createdAt)) {
      setThread(storedThread);
      setIsEmailValidated(storedThread.hasValidatedEmail);
    } else {
      initializeThread();
    }
  }, [knowledgeBaseId, assistantSettings, initializeThread, isThreadExpired, channel]);

  const useUpdateThreadStorage = (validatedEmail: string, channel: AssistantChannel, knowledgeBaseId: number) => {
    useEffect(() => {
      const storageKey = `assistantThread-${channel}-${knowledgeBaseId}`;
      const storedThread = JSON.parse(getLocalStorageItem(storageKey) || "{}");

      const updateThread: Thread = {
        ...storedThread,
        hasValidatedEmail: storedThread.hasValidatedEmail || validatedEmail !== "",
      };

      setLocalStorageItem(storageKey, JSON.stringify(updateThread));
    }, [validatedEmail, channel, knowledgeBaseId]);
  };

  return {thread, initializeThread, channel, isEmailValidated, validateEmail, useUpdateThreadStorage};
};

export const useMessages = (
  knowledgeBaseId: number,
  thread: Thread | null,
  assistantId: string,
  channel: AssistantChannel,
) => {
  const [messages, setMessages] = useState<AssistantChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchMessages = useCallback(async () => {
    if (!thread || !thread.threadId) return;

    setIsLoading(true);
    try {
      const response = await GetAssistantMessages(knowledgeBaseId, thread.threadId);
      setMessages(
        response?.data?.messages?.map((msg: any) => {
          return {
            text: msg.content,
            sender: msg.role,
            createdAt: new Date(msg.created_at * 1000).toISOString(),
          };
        }) || [],
      );
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
    setIsLoading(false);
  }, [knowledgeBaseId, thread]);

  const sendMessage = useCallback(
    async (message: string, assistantId: string | undefined) => {
      if (!thread || !assistantId) return;

      setMessages((prev) => [{text: message, sender: "user", createdAt: Date.now()}, ...prev]);
      setTimeout(() => {
        setIsLoading(true);
      }, 1000);
      try {
        await PostMessageToAssistant(knowledgeBaseId, assistantId, message, thread.threadId);
        await fetchMessages();
      } catch (error) {
        console.error("Error sending/receiving message:", error);
      }
      setIsLoading(false);
    },
    [thread, knowledgeBaseId, fetchMessages],
  );

  useEffect(() => {
    if (thread) {
      fetchMessages();
    }
  }, [thread, fetchMessages]);

  useEffect(() => {
    if (messages.length > 0) {
      const formattedMessages: ConversationMessage[] = messages.map(({createdAt, ...rest}) => ({
        ...rest,
        timestamp: createdAt.toString(),
      }));
      UpsertConversationLog(knowledgeBaseId, assistantId, thread?.threadId, {
        messages: formattedMessages,
        channel: channel,
      });
    }
  }, [messages, knowledgeBaseId, assistantId, thread?.threadId, channel]);

  return {messages, setMessages, isLoading, sendMessage};
};

export const useAssistantChat = () => {
  const useScrollToBottom = (ref: React.RefObject<HTMLDivElement>, dependencies: React.DependencyList) => {
    useEffect(() => {
      if (ref.current) {
        ref.current.scrollTop = ref.current.scrollHeight;
      }
    }, [ref, dependencies]);
  };

  const endConversation = (
    messages: any[],
    channel: AssistantChannel,
    knowledgeBaseId: number,
    assistantId: string,
    threadId: string,
  ) => {
    if (messages.length > 0) {
      const formattedMessages: ConversationMessage[] = messages.map(({createdAt, ...rest}) => ({
        ...rest,
        timestamp: createdAt.toString(),
      }));
      UpsertConversationLog(knowledgeBaseId, assistantId, threadId, {
        messages: formattedMessages,
        status: ConversationLogStatus.COMPLETED,
        channel: channel,
      });
    }
  };

  const useExpandFeedbackSection = (messages: any[], setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>) => {
    useEffect(() => {
      if (messages.length === 2) {
        const timer = setTimeout(() => setIsCollapsed(false), 1500);
        return () => clearTimeout(timer);
      }
    }, [messages, setIsCollapsed]);
  };

  return {useScrollToBottom, useExpandFeedbackSection, endConversation};
};
